import { useEffect, useState, type ReactNode } from "react";
import { useOrganizationStore } from "../../stores/useOrganizationStore";
import { useOrganizations } from "../../data/hooks/useOrganizations";
import useAvailableFacilityComponents from "../../data/hooks/useAvailableFacilityComponents";
import {
  isFacilityDeactivatedError,
  isFacilityInaccessibleError,
} from "../../lib/apiError";
import FacilityDeactivatedPage from "../shared/FacilityDeactivatedPage";
import FacilityNotFoundPage from "../shared/FacilityNotFoundPage";
import { Spinner } from "../ui/spinner";
import type { Organization } from "../../data/types";

type RequireFacilityProps = {
  /** Org and facility IDs from the URL — the facility being gated. */
  orgId: number;
  facilityId: number;
  children: ReactNode;
};

// Prefers the URL org's first facility so the way back stays in the same
// organization, else the first facility of any organization.
function findFallbackFacilityPath(
  organizations: Organization[],
  orgId: number,
): string | undefined {
  const urlOrganization = organizations.find(
    (organization) => organization.id === orgId,
  );
  const fallbackOrganization = urlOrganization?.facilities?.length
    ? urlOrganization
    : organizations.find((organization) => organization.facilities?.length);
  const fallbackFacility = fallbackOrganization?.facilities[0];
  if (!fallbackOrganization || !fallbackFacility) return undefined;
  return `/organizations/${fallbackOrganization.id}/facilities/${fallbackFacility.id}/overview`;
}

export default function RequireFacility({
  orgId,
  facilityId,
  children,
}: RequireFacilityProps) {
  const [hydrated, setHydrated] = useState(
    () => useOrganizationStore.persist.hasHydrated(),
  );
  const activeFacilityId = useOrganizationStore((s) => s.activeFacilityId);

  // Reuses the same ["organizations"] query Sidebar.tsx already subscribes
  // to — this adds an observer, not a second network request. Blocking on
  // its first load (not just on the persisted Zustand snapshot) prevents
  // pages from rendering with a stale/incomplete facility — e.g. missing
  // telemetryStartDate for the live telemetry date picker — from a previous
  // session, before the fresh Organizations response has arrived.
  const { organizations, loading: organizationsLoading } = useOrganizations();

  const isListedFacility = organizations.some(
    (organization) =>
      organization.id === orgId &&
      organization.facilities?.some((facility) => facility.id === facilityId),
  );

  // A facility missing from the (active-only) organizations list is either
  // deactivated or doesn't exist / isn't accessible to this user — the list
  // alone can't tell which. One probe request decides before any page (and
  // its burst of facility requests) mounts. Same query key AppLayout already
  // fetches for the active facility, so this observes that request rather
  // than adding one.
  const needsProbe = hydrated && !organizationsLoading && !isListedFacility;
  const facilityProbe = useAvailableFacilityComponents(facilityId, needsProbe);

  useEffect(() => {
    if (hydrated) return;
    const unsub = useOrganizationStore.persist.onFinishHydration(() =>
      setHydrated(true),
    );
    return unsub;
  }, [hydrated]);

  if (
    !hydrated ||
    !activeFacilityId ||
    organizationsLoading ||
    (needsProbe && facilityProbe.isLoading)
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  if (needsProbe && facilityProbe.isError) {
    if (isFacilityDeactivatedError(facilityProbe.error)) {
      return <FacilityDeactivatedPage />;
    }
    if (isFacilityInaccessibleError(facilityProbe.error)) {
      return (
        <FacilityNotFoundPage
          fallbackFacilityPath={findFallbackFacilityPath(organizations, orgId)}
        />
      );
    }
  }

  return <>{children}</>;
}
